import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { Plus, Send, Trash2, Mail, X, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function SendGroceryDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: endpoints.contacts,
    enabled: open,
  });

  const [selected, setSelected] = useState(new Set());
  const [adhoc, setAdhoc] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const createContact = useMutation({
    mutationFn: endpoints.createContact,
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      setSelected((s) => new Set([...s, c.id]));
      setNewName("");
      setNewEmail("");
      setShowAdd(false);
      toast.success(`Added ${c.name}`);
    },
    onError: () => toast.error("Could not save contact"),
  });

  const delContact = useMutation({
    mutationFn: endpoints.deleteContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
  });

  const sendMut = useMutation({
    mutationFn: endpoints.sendGrocery,
    onSuccess: (data) => {
      toast.success(`Sent to ${data.recipients.length} recipient${data.recipients.length === 1 ? "" : "s"}`);
      onOpenChange(false);
      setSelected(new Set());
      setAdhoc("");
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "Send failed";
      toast.error(detail);
    },
  });

  const totalRecipients = useMemo(() => {
    const emails = adhoc
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes("@"));
    return selected.size + emails.length;
  }, [selected, adhoc]);

  function toggle(id) {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function send() {
    if (totalRecipients === 0) return toast.error("Pick a recipient");
    const emails = adhoc
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes("@"));
    sendMut.mutate({
      contact_ids: [...selected],
      emails,
      include_checked: false,
    });
  }

  function saveContact() {
    if (!newName.trim() || !newEmail.includes("@"))
      return toast.error("Name and valid email required");
    createContact.mutate({ name: newName.trim(), email: newEmail.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#23211F] border-[#33302C] text-[#EAE4DB] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Mail size={20} className="text-[#D07A65]" /> Send grocery list
          </DialogTitle>
          <DialogDescription className="text-[#A6A098]">
            Email the unchecked items to someone. Only unchecked items are included.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Contacts list */}
          {contacts.length > 0 && (
            <div>
              <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
                Contacts
              </div>
              <ul data-testid="contacts-list" className="space-y-1">
                {contacts.map((c) => {
                  const checked = selected.has(c.id);
                  return (
                    <li
                      key={c.id}
                      data-testid={`contact-row-${c.id}`}
                      className="group flex items-center gap-2.5 p-2 rounded-xl hover:bg-[#171514] transition"
                    >
                      <button
                        data-testid={`select-contact-${c.id}`}
                        onClick={() => toggle(c.id)}
                        className="w-8 h-8 rounded-full border-2 flex items-center justify-center transition shrink-0"
                        style={{
                          borderColor: checked ? "#D07A65" : "#33302C",
                          backgroundColor: checked ? "#D07A65" : "transparent",
                        }}
                      >
                        {checked && (
                          <Check size={14} className="text-[#171514]" strokeWidth={3} />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#EAE4DB] truncate">{c.name}</div>
                        <div className="text-xs text-[#A6A098] truncate">{c.email}</div>
                      </div>
                      <button
                        data-testid={`delete-contact-${c.id}`}
                        onClick={() => delContact.mutate(c.id)}
                        className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-full text-[#A6A098] hover:text-[#C85A5A] hover:bg-[#2D2A28] flex items-center justify-center transition"
                        aria-label="Remove"
                      >
                        <Trash2 size={13} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Add contact */}
          {showAdd ? (
            <div className="border border-[#33302C] rounded-xl p-3 space-y-2 bg-[#171514]">
              <div className="flex items-center justify-between">
                <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098]">
                  New contact
                </div>
                <button
                  onClick={() => setShowAdd(false)}
                  className="text-[#A6A098] hover:text-[#EAE4DB]"
                  aria-label="Cancel"
                >
                  <X size={14} />
                </button>
              </div>
              <input
                data-testid="new-contact-name"
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full h-9 px-3 rounded-lg bg-[#23211F] border border-[#33302C] text-sm text-[#EAE4DB] placeholder:text-[#5a544e] focus:outline-none focus:border-[#D07A65]"
              />
              <input
                data-testid="new-contact-email"
                placeholder="email@example.com"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full h-9 px-3 rounded-lg bg-[#23211F] border border-[#33302C] text-sm text-[#EAE4DB] placeholder:text-[#5a544e] focus:outline-none focus:border-[#D07A65]"
              />
              <button
                data-testid="save-contact-btn"
                onClick={saveContact}
                disabled={createContact.isPending}
                className="w-full h-9 rounded-lg bg-[#D07A65] text-[#171514] text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition"
              >
                Save contact
              </button>
            </div>
          ) : (
            <button
              data-testid="add-contact-btn"
              onClick={() => setShowAdd(true)}
              className="w-full h-10 rounded-xl border border-dashed border-[#33302C] text-[#A6A098] hover:text-[#EAE4DB] hover:border-[#5a544e] text-sm flex items-center justify-center gap-2 transition"
            >
              <Plus size={14} /> Add contact
            </button>
          )}

          {/* Ad-hoc email */}
          <div>
            <div className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#A6A098] mb-1.5">
              Or one-off email
            </div>
            <input
              data-testid="adhoc-email"
              placeholder="someone@example.com"
              value={adhoc}
              onChange={(e) => setAdhoc(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-[#171514] border border-[#33302C] text-sm text-[#EAE4DB] placeholder:text-[#5a544e] focus:outline-none focus:border-[#D07A65]"
            />
          </div>
        </div>

        <DialogFooter className="mt-3">
          <button
            data-testid="send-grocery-btn"
            onClick={send}
            disabled={sendMut.isPending || totalRecipients === 0}
            className="w-full h-11 rounded-full bg-[#D07A65] text-[#171514] font-semibold uppercase tracking-wider hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={16} />
            {sendMut.isPending
              ? "Sending…"
              : `Send to ${totalRecipients || "—"} recipient${totalRecipients === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
